import {
  foundationBrickLedges,
  foundationCurbs,
  foundationFootings,
  foundationPiers,
  foundationSlabs,
  foundationStemWalls,
} from "building-generation/elements/foundation/foundationProfiles";
import { isIsolatedFoundationSection } from "building-generation/elements/foundation/foundationStrip";
import type { Building } from "building-types/building/buildingTypes";
import type { IsolatedFoundationSectionSize } from "building-types/foundations/foundationTypes";
import type { BuildingGeometryEmitter } from "building-types/geometry/buildingGeometryTypes";
import type { DocEntityHandle, EdgeBehaviorHandle } from "building-types/handles/docEntityHandleTypes";
import { FaceCategory } from "building-types/materials/faceCategories";
import { RoomClass } from "building-types/rooms/roomTypesEnum";
import { MS_Floor_Concrete__Default } from "common/materials/library/designMaterialSrcDefaults";
import { materialSrcFromPalette } from "doc/materialLookup";
import type { Extrusion } from "geom/brep";
import { faceOrNullMoveAlongNormal } from "geom/brep";
import { BSP_EPSILON } from "geom/bspTypes";
import { extrusionToBRep } from "geom/extrude";
import { polyLine2CompactnessPolsbyPopper } from "geom/polyLine2";
import { UVGeneration } from "geom/uv";
import { buildExteriorLayers } from "./buildingGeometryTools";

type BuildingForFoundationGeometries = Pick<
  Building,
  "foundationSuperSystems" | "levels" | "exteriorPalette" | "foundations"
>;

function isCircular(ext: Extrusion): boolean {
  return polyLine2CompactnessPolsbyPopper(ext.profile.loops[0]) > 0.99;
}

export function getFoundationSlabGeometries(
  building: BuildingForFoundationGeometries,
  emitter: BuildingGeometryEmitter,
): void {
  const { foundationSuperSystems, levels, exteriorPalette } = building;

  const threeLayers = buildExteriorLayers();

  const material = "foundation0";
  const materialSrc = materialSrcFromPalette(exteriorPalette, material);

  for (const foundationId in foundationSuperSystems) {
    const foundationSuperSystem = foundationSuperSystems[foundationId];
    const foundationLevel = levels[foundationSuperSystem.levelId];

    if (!foundationLevel) {
      continue;
    }

    const entityHandleForSelectingReportingUnit = foundationSuperSystem.docEntityHandle;

    for (const slab of foundationSlabs(foundationSuperSystem)) {
      const brep = extrusionToBRep(slab.extrusion);

      // fix known conflict between coincident BSP edges.
      // porches and patios often match up exactly with the house and garage slabs
      if (slab.roomClass > RoomClass.GARAGE) {
        faceOrNullMoveAlongNormal(brep.faces[0], BSP_EPSILON * 2);
        faceOrNullMoveAlongNormal(brep.faces[1], BSP_EPSILON * 2);
      }

      emitter.emit({
        geom: brep,
        faceCategory: FaceCategory.FOUNDATION_SLAB,
        threeLayers,
        entityId: undefined,
        levelId: foundationLevel.id,
        pickHandle: foundationSuperSystem.pickHandle,
        entityHandleForSelectingReportingUnit,
        materialSlot: material,
        materialSrc: materialSrc,
        uvGen: UVGeneration.GLOBAL_RECT_UV,
      });
    }
  }
}

export function getFoundationFootingGeometries(
  building: BuildingForFoundationGeometries,
  emitter: BuildingGeometryEmitter,
): void {
  const { foundations, levels, exteriorPalette } = building;

  const threeLayers = buildExteriorLayers();

  for (const foundationId in foundations) {
    const foundationSubSystem = foundations[foundationId];
    const foundationLevel = levels[foundationSubSystem.levelId];

    if (!foundationLevel) {
      continue;
    }

    const entityHandleForSelectingReportingUnit = foundationSubSystem.docEntityHandle;
    if (!entityHandleForSelectingReportingUnit) {
      throw new Error(`Missing docEntityHandle for foundation ${foundationId} while emitting footing geometry.`);
    }

    for (const footing of foundationFootings(foundationSubSystem)) {
      const { material } = footing;
      const materialSrc = footing.material
        ? materialSrcFromPalette(exteriorPalette, footing.material)
        : MS_Floor_Concrete__Default;

      // Keep isolated footings selectable by their source, unless they belong to a component instance.
      const pickHandle = isIsolatedFoundationSection(footing)
        ? getIsolatedFoundationPickHandle(footing.size, foundationSubSystem.pickHandle)
        : foundationSubSystem.pickHandle;

      for (const ext of footing.extrusions) {
        emitter.emit({
          geom: ext,
          faceCategory: FaceCategory.FOUNDATION_FOOTING,
          threeLayers,
          entityId: undefined,
          entityHandleForSelectingReportingUnit,
          levelId: foundationLevel.id,
          pickHandle,
          materialSlot: material ?? "foundation0",
          materialSrc: materialSrc,
          uvGen: UVGeneration.GLOBAL_RECT_UV,
          generateNeighbors: isCircular(ext), // This ensures that circular footings are rendered without excessive edges
        });
      }
    }
  }
}

export function getFoundationStemWallGeometries(
  building: BuildingForFoundationGeometries,
  emitter: BuildingGeometryEmitter,
): void {
  const { foundations, levels, exteriorPalette } = building;

  const threeLayers = buildExteriorLayers();
  const materialSrc = materialSrcFromPalette(exteriorPalette, "foundation0");

  for (const foundationId in foundations) {
    const foundationSubSystem = foundations[foundationId];
    const foundationLevel = levels[foundationSubSystem.levelId];

    if (!foundationLevel) {
      continue;
    }

    const entityHandleForSelectingReportingUnit = foundationSubSystem.docEntityHandle;
    if (!entityHandleForSelectingReportingUnit) {
      throw new Error(`Missing docEntityHandle for foundation ${foundationId} while emitting stem wall geometry.`);
    }

    for (const stemWall of foundationStemWalls(foundationSubSystem)) {
      for (const ext of stemWall.extrusions) {
        emitter.emit({
          geom: ext,
          faceCategory: FaceCategory.FOUNDATION_STEM_WALL,
          threeLayers,
          entityId: undefined,
          entityHandleForSelectingReportingUnit,
          levelId: foundationLevel.id,
          pickHandle: foundationSubSystem.pickHandle,
          materialSlot: "foundation0",
          materialSrc: materialSrc,
          uvGen: UVGeneration.GLOBAL_RECT_UV,
        });
      }
    }
  }
}

export function getFoundationCurbGeometries(
  building: BuildingForFoundationGeometries,
  emitter: BuildingGeometryEmitter,
): void {
  const { foundations, levels, exteriorPalette } = building;

  const threeLayers = buildExteriorLayers();

  for (const foundationId in foundations) {
    const foundationSubSystem = foundations[foundationId];
    const foundationLevel = levels[foundationSubSystem.levelId];

    if (!foundationLevel) {
      continue;
    }

    const entityHandleForSelectingReportingUnit = foundationSubSystem.docEntityHandle;
    if (!entityHandleForSelectingReportingUnit) {
      throw new Error(`Missing docEntityHandle for foundation ${foundationId} while emitting curb geometry.`);
    }

    for (const curb of foundationCurbs(foundationSubSystem)) {
      const material = curb.material ?? "foundation0";
      const materialSrc = materialSrcFromPalette(exteriorPalette, material);
      for (const ext of curb.extrusions) {
        emitter.emit({
          geom: ext,
          faceCategory: FaceCategory.FOUNDATION_GARAGE_CURB,
          threeLayers,
          entityId: undefined,
          entityHandleForSelectingReportingUnit,
          levelId: foundationLevel.id,
          pickHandle: foundationSubSystem.pickHandle,
          materialSlot: material,
          materialSrc: materialSrc,
          uvGen: UVGeneration.GLOBAL_RECT_UV,
        });
      }
    }

    for (const ledge of foundationBrickLedges(foundationSubSystem)) {
      const material = ledge.material ?? "foundation0";
      const materialSrc = materialSrcFromPalette(exteriorPalette, material);
      for (const ext of ledge.extrusions) {
        emitter.emit({
          geom: ext,
          faceCategory: FaceCategory.FOUNDATION_GARAGE_CURB,
          threeLayers,
          entityId: undefined,
          entityHandleForSelectingReportingUnit,
          levelId: foundationLevel.id,
          pickHandle: foundationSubSystem.pickHandle,
          materialSlot: material,
          materialSrc: materialSrc,
          uvGen: UVGeneration.GLOBAL_RECT_UV,
        });
      }
    }
  }
}

/**
 * Returns the appropriate pick handle for an isolated foundation section.
 * Component instance geometry always selects the instance as a whole. Outside an instance,
 * generated foundations use their source entity's handle and authored patterns use an
 * EdgeBehavior handle. Foundations without either association fall back to the foundation
 * system's pick handle.
 */
export function getIsolatedFoundationPickHandle(
  sectionSize: Pick<IsolatedFoundationSectionSize, "pickSource">,
  fallbackHandle: DocEntityHandle | undefined,
): DocEntityHandle | undefined {
  if (fallbackHandle?.type === "COMPONENT_INSTANCE") {
    return fallbackHandle;
  }

  const { pickSource } = sectionSize;
  if (pickSource?.kind === "sourceEntity") {
    return pickSource.sourcePickHandle;
  }
  if (pickSource?.kind === "edgeBehavior") {
    const edgeBehaviorHandle: EdgeBehaviorHandle = {
      type: "EDGE_BEHAVIOR",
      id: pickSource.edgeBehaviorId,
    };
    return edgeBehaviorHandle;
  }
  return fallbackHandle;
}

export function getFoundationPierGeometries(
  building: BuildingForFoundationGeometries,
  emitter: BuildingGeometryEmitter,
): void {
  const { foundations, levels, exteriorPalette } = building;

  const threeLayers = buildExteriorLayers();

  for (const foundationId in foundations) {
    const foundationSubSystem = foundations[foundationId];
    const foundationLevel = levels[foundationSubSystem.levelId];

    if (!foundationLevel) {
      continue;
    }

    const entityHandleForSelectingReportingUnit = foundationSubSystem.docEntityHandle;
    if (!entityHandleForSelectingReportingUnit) {
      throw new Error(`Missing docEntityHandle for foundation ${foundationId} while emitting pier geometry.`);
    }

    for (const pier of foundationPiers(foundationSubSystem)) {
      const material = pier.material ?? "foundation0";
      const materialSrc = materialSrcFromPalette(exteriorPalette, material);

      // Keep isolated piers selectable by their source, unless they belong to a component instance.
      const pickHandle = isIsolatedFoundationSection(pier)
        ? getIsolatedFoundationPickHandle(pier.size, foundationSubSystem.pickHandle)
        : foundationSubSystem.pickHandle;

      for (const ext of pier.extrusions) {
        emitter.emit({
          geom: ext,
          faceCategory: FaceCategory.FOUNDATION_PIER,
          threeLayers,
          entityId: undefined,
          entityHandleForSelectingReportingUnit,
          levelId: foundationLevel.id,
          pickHandle,
          materialSlot: material,
          materialSrc: materialSrc,
          uvGen: UVGeneration.GLOBAL_RECT_UV,
          generateNeighbors: isCircular(ext), // This ensures that circular piers are rendered without excessive edges
        });
      }
    }
  }
}
